import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore'
import { User } from 'firebase/auth'
import { db } from '@/lib/firebase'
import {
  LockedSalaryData,
  DriverInfoSnapshot,
  AggregatedTotalsSnapshot,
  CompanyInfoSnapshot,
} from '@/types/lockedSalaryData'
import { SalaryCalculationConfig } from './salaryCalculationService'
import { SalarySnapshotService } from './salarySnapshotService'
import { EarningsService } from './earningsService'
import { CompensationService } from './compensationService'
import { DriverMasterService } from './driverMasterService'
import { payoutCompanyService } from './payoutCompanyService'
import { userProfileService } from './userProfileService'
import { useConfigStore } from '@/stores/configStore'

/**
 * Helper to remove undefined values from an object (shallow)
 * Firestore doesn't accept undefined values - they must be omitted or null
 */
function removeUndefined<T extends object>(obj: T): Partial<T> {
  const result: Partial<T> = {}
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
      result[key] = obj[key]
    }
  }
  return result
}

/**
 * Service for managing locked salary data snapshots.
 * Creates immutable snapshots of salary calculations when periods are locked.
 *
 * Collection path: users/{userId}/locked_salary_data/{driverId}_{period}
 * Document ID format: "{driverId}_{period}" (e.g., "driver123_2025-11-01")
 */
export class LockedSalaryDataService {
  private static readonly COLLECTION_NAME = 'locked_salary_data'

  /**
   * Get collection reference for a user
   */
  private static getUserCollection(user: User) {
    return collection(db, 'users', user.uid, this.COLLECTION_NAME)
  }

  /**
   * Get document reference with auto-generated ID for a new snapshot
   */
  private static getNewSnapshotDocument(user: User) {
    return doc(collection(db, 'users', user.uid, this.COLLECTION_NAME))
  }

  /**
   * Get document reference for a specific snapshot by ID
   */
  private static getSnapshotDocumentById(user: User, snapshotId: string) {
    return doc(db, 'users', user.uid, this.COLLECTION_NAME, snapshotId)
  }

  /**
   * Convert Firestore document to LockedSalaryData with proper typing
   */
  private static convertFirestoreDoc(doc: QueryDocumentSnapshot<DocumentData> | DocumentData, docId?: string): LockedSalaryData {
    const data = 'data' in doc ? doc.data() : doc
    const id = docId || ('id' in doc ? doc.id : data.id)

    return {
      id,
      driverId: data.driverId,
      period: data.period,
      createdAt: data.createdAt ? (data.createdAt as Timestamp).toDate() : new Date(),
      schemaVersion: data.schemaVersion,
      rawEarnings: data.rawEarnings || [],
      aggregatedTotals: data.aggregatedTotals,
      compensations: data.compensations || [],
      fleetSettingsUsed: data.fleetSettingsUsed,
      driverTaxSettings: data.driverTaxSettings,
      totalGrossSalary: data.totalGrossSalary || 0,
      totalNetAfterTax: data.totalNetAfterTax || 0,
      driverInfo: data.driverInfo,
      companyInfo: data.companyInfo,
      pdfStoragePath: data.pdfStoragePath,
      pdfGeneratedAt: data.pdfGeneratedAt ? (data.pdfGeneratedAt as Timestamp).toDate() : undefined,
      emailSentAt: data.emailSentAt ? (data.emailSentAt as Timestamp).toDate() : undefined,
      isActive: data.isActive ?? true, // Default to true for backwards compatibility
      deactivatedAt: data.deactivatedAt ? (data.deactivatedAt as Timestamp).toDate() : undefined,
      deactivationReason: data.deactivationReason,
    }
  }

  /**
   * Get fleet settings in the format expected by SalaryCalculationService
   * Converts percentage values from store (0-100) to decimals (0-1)
   */
  private static getFleetSettings(): SalaryCalculationConfig {
    const settings = useConfigStore.getState().settings

    return {
      driverRevenueShare: settings.driverRevenueShare / 100,  // 40 → 0.40
      vacationPayRate: settings.vacationPayRate / 100,        // 12.5 → 0.125
      vatRate: settings.vatRate / 100,                        // 6 → 0.06
      taxRate: settings.taxRate / 100,                        // 30 → 0.30
      feeOnTips: settings.feeOnTips || 'fleet_commission',
      taxCalculationMethod: settings.taxCalculationMethod || 'fixed',
      taxTableName: settings.taxTableName || 'skattetabell31',
      taxColumn: settings.taxColumn ?? 1,
      employerTaxRate: (settings.employerTaxRate || 31.42) / 100, // 31.42 → 0.3142
      revenueShareMode: settings.revenueShareMode ?? 'single',
      revenueShareTiers: settings.revenueShareTiers,
    }
  }

  /**
   * Create a complete salary snapshot for a driver in a specific period
   * This is the main method called during period locking
   *
   * IMPORTANT: This method will deactivate any existing active snapshots for the same
   * driver+period before creating a new one. This ensures only one active snapshot exists.
   *
   * Now uses SalarySnapshotService as the single source of truth for salary calculations.
   *
   * Steps:
   * 1. Check for existing active snapshot and deactivate if found
   * 2. Use SalarySnapshotService to create complete snapshot (handles all calculations)
   * 3. Map snapshot to Firestore document format
   * 4. Save to Firestore with metadata
   * 5. Return created snapshot
   */
  static async createSnapshot(
    user: User,
    driverId: string,
    period: string
  ): Promise<LockedSalaryData> {
    try {
      // 1. Check for existing active snapshot and deactivate it
      const existing = await this.getLockedData(user, driverId, period)
      if (existing) {
        await this.deactivateSnapshot(
          user,
          existing.id,
          'Automatiskt inaktiverad vid omlåsning av period'
        )
      }

      // 2. Create complete salary snapshot using SalarySnapshotService
      // This handles all earnings fetching, calculations, and aggregation
      const snapshot = await SalarySnapshotService.createSnapshotFromDatabase(
        user,
        driverId,
        period
      )

      // 3. Resolve company info for snapshot
      // Priority: driver's assigned payout company → UserProfile fallback
      let companyInfo: CompanyInfoSnapshot | undefined
      const driver = await DriverMasterService.getDriver(user, driverId)
      if (driver?.payoutCompanyId) {
        const payoutCompany = await payoutCompanyService.getPayoutCompany(user.uid, driver.payoutCompanyId)
        if (payoutCompany) {
          companyInfo = {
            name: payoutCompany.name || undefined,
            orgNumber: payoutCompany.organizationNumber || undefined,
            address: payoutCompany.address || undefined,
            city: payoutCompany.city || undefined,
            postalCode: payoutCompany.postalCode || undefined,
          }
        }
      }
      if (!companyInfo) {
        const userProfile = await userProfileService.getUserProfile(user.uid)
        if (userProfile) {
          companyInfo = {
            name: userProfile.companyName || undefined,
            orgNumber: userProfile.organizationNumber || undefined,
            address: userProfile.address || undefined,
            city: userProfile.city || undefined,
            postalCode: userProfile.postalCode || undefined,
          }
        }
      }

      // 5. Create snapshot document with auto-generated ID
      const docRef = this.getNewSnapshotDocument(user)

      // 6. Map snapshot to Firestore document format
      // Store aggregatedTotals for correct tax calculation on combined amount
      const aggregatedTotals: AggregatedTotalsSnapshot = {
        grossEarnings: snapshot.totals.grossEarnings,
        netEarnings: snapshot.totals.netEarnings,
        tips: snapshot.totals.tips,
        cashReceived: snapshot.totals.cashReceived,
        vatAmount: snapshot.totals.vatAmount,
        shareableAmount: snapshot.totals.shareableAmount,
        afterFleetCommission: snapshot.totals.afterFleetCommission,
        vacationPay: snapshot.totals.vacationPay,
        appliedDriverRevenueShare: snapshot.totals.appliedDriverRevenueShare,
        grossSalary: snapshot.totals.grossSalary,
        finalGrossSalary: snapshot.totals.finalGrossSalary,
        employerTaxAmount: snapshot.totals.employerTaxAmount,
        totalEmployerCost: snapshot.totals.totalEmployerCost,
        netSalary: snapshot.totals.netSalary,
        finalNetSalary: snapshot.totals.finalNetSalary,
        taxAmount: snapshot.totals.taxAmount,
        effectiveTaxRate: snapshot.totals.effectiveTaxRate,
        untaxedTips: snapshot.totals.untaxedTips,
        // Legacy compat: stale clients still compute payout as
        // finalNetPayout - cashReceived. Remove after rollout.
        finalNetPayout: Math.round((snapshot.totals.amountToPay + snapshot.totals.cashReceived) * 100) / 100,
        amountToPay: snapshot.totals.amountToPay,
        netEarningsCompensations: snapshot.totals.netEarningsCompensations,
        netEarningsDeductions: snapshot.totals.netEarningsDeductions,
        grossSalaryCompensations: snapshot.totals.grossSalaryCompensations,
        grossSalaryDeductions: snapshot.totals.grossSalaryDeductions,
        netSalaryCompensations: snapshot.totals.netSalaryCompensations,
        netSalaryDeductions: snapshot.totals.netSalaryDeductions,
      }

      // Sanitize compensations to remove undefined values (Firestore doesn't accept undefined)
      const sanitizedCompensations = snapshot.compensations.map(comp => removeUndefined(comp))

      // Sanitize fleetSettings to ensure no undefined values
      const sanitizedFleetSettings = removeUndefined(snapshot.fleetSettings)

      // Sanitize driverTaxSettings if present
      const sanitizedDriverTaxSettings = snapshot.driverTaxSettings
        ? removeUndefined(snapshot.driverTaxSettings)
        : undefined

      // Only include defined fields to avoid Firestore undefined value errors
      await setDoc(docRef, {
        driverId: snapshot.driverId,
        period: snapshot.period,
        schemaVersion: 2,
        rawEarnings: snapshot.rawEarnings,
        aggregatedTotals,
        compensations: sanitizedCompensations,
        fleetSettingsUsed: sanitizedFleetSettings,
        ...(sanitizedDriverTaxSettings && Object.keys(sanitizedDriverTaxSettings).length > 0 && {
          driverTaxSettings: sanitizedDriverTaxSettings
        }),
        totalGrossSalary: snapshot.totals.finalGrossSalary,
        totalNetAfterTax: snapshot.totals.finalNetSalary,
        driverInfo: {
          name: snapshot.driverInfo.name,
          ...(snapshot.driverInfo.employeeNumber && { employeeNumber: snapshot.driverInfo.employeeNumber }),
          ...(snapshot.driverInfo.email && { email: snapshot.driverInfo.email }),
          ...(snapshot.driverInfo.personalCode && { personalCode: snapshot.driverInfo.personalCode }),
          ...(snapshot.driverInfo.bankAccount && { bankAccount: snapshot.driverInfo.bankAccount }),
        },
        ...(companyInfo && { companyInfo: removeUndefined(companyInfo) }),
        isActive: true,
        createdAt: serverTimestamp(),
      })

      // 7. Fetch and return the created snapshot
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) {
        throw new Error('Det gick inte att skapa snapshot')
      }

      return this.convertFirestoreDoc(docSnap)
    } catch (error) {
      console.error('Error creating salary snapshot:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att skapa löne-snapshot')
    }
  }

  /**
   * Get active locked salary data for a specific driver and period
   * Returns null if no active snapshot exists
   */
  static async getLockedData(
    user: User,
    driverId: string,
    period: string
  ): Promise<LockedSalaryData | null> {
    try {
      const q = query(
        this.getUserCollection(user),
        where('driverId', '==', driverId),
        where('period', '==', period),
        where('isActive', '==', true)
      )

      const querySnapshot = await getDocs(q)

      if (querySnapshot.empty) {
        return null
      }

      // Should only be one active snapshot per driver+period
      if (querySnapshot.size > 1) {
        console.warn(
          `Found ${querySnapshot.size} active snapshots for driver ${driverId} in period ${period}. ` +
          'Expected only 1. Returning the most recent one.'
        )
      }

      // Return the most recent if multiple found (shouldn't happen)
      const docs = querySnapshot.docs.sort((a, b) => {
        const aTime = a.data().createdAt?.toMillis() || 0
        const bTime = b.data().createdAt?.toMillis() || 0
        return bTime - aTime
      })

      return this.convertFirestoreDoc(docs[0])
    } catch (error) {
      console.error('Error fetching locked salary data:', error)
      throw new Error('Det gick inte att hämta låst lönedata')
    }
  }

  /**
   * Get all locked salary data for a specific period
   * By default only returns active snapshots, but can include inactive ones
   */
  static async getLockedDataForPeriod(
    user: User,
    period: string,
    includeInactive: boolean = false
  ): Promise<LockedSalaryData[]> {
    try {
      const q = includeInactive
        ? query(
            this.getUserCollection(user),
            where('period', '==', period)
          )
        : query(
            this.getUserCollection(user),
            where('period', '==', period),
            where('isActive', '==', true)
          )

      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map(doc => this.convertFirestoreDoc(doc))
    } catch (error) {
      console.error('Error fetching locked salary data for period:', error)
      throw new Error('Det gick inte att hämta låst lönedata för perioden')
    }
  }

  /**
   * Deactivate a locked salary data snapshot by its ID
   * Used when period is unlocked or when creating a new snapshot for the same driver+period
   */
  static async deactivateSnapshot(
    user: User,
    snapshotId: string,
    reason: string
  ): Promise<void> {
    try {
      if (!reason || reason.trim().length === 0) {
        throw new Error('Anledning för inaktivering är obligatorisk')
      }

      const docRef = this.getSnapshotDocumentById(user, snapshotId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('Snapshot hittades inte')
      }

      await updateDoc(docRef, {
        isActive: false,
        deactivatedAt: serverTimestamp(),
        deactivationReason: reason.trim(),
      })
    } catch (error) {
      console.error('Error deactivating locked salary data:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Det gick inte att inaktivera snapshot')
    }
  }

  /**
   * Deactivate all active snapshots for a period (used during unlock)
   * Uses parallel execution for better performance with multiple snapshots
   */
  static async deactivateAllForPeriod(
    user: User,
    period: string,
    reason: string
  ): Promise<number> {
    try {
      const snapshots = await this.getLockedDataForPeriod(user, period, false)

      // Deactivate all snapshots in parallel for better performance
      const deactivatePromises = snapshots.map(snapshot =>
        this.deactivateSnapshot(user, snapshot.id, reason)
          .catch(err => {
            console.error(`Failed to deactivate snapshot ${snapshot.id} for driver ${snapshot.driverId}:`, err)
            return null
          })
      )

      const results = await Promise.all(deactivatePromises)
      const deactivatedCount = results.filter(r => r !== null).length

      return deactivatedCount
    } catch (error) {
      console.error('Error deactivating all snapshots for period:', error)
      throw new Error('Det gick inte att inaktivera snapshots för perioden')
    }
  }

  /**
   * Update PDF tracking information after PDF is generated
   */
  static async updatePdfInfo(
    user: User,
    snapshotId: string,
    pdfStoragePath: string
  ): Promise<void> {
    try {
      const docRef = this.getSnapshotDocumentById(user, snapshotId)

      await updateDoc(docRef, {
        pdfStoragePath,
        pdfGeneratedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Error updating PDF info:', error)
      throw new Error('Det gick inte att uppdatera PDF-information')
    }
  }

  /**
   * Update email sent timestamp
   */
  static async updateEmailSent(
    user: User,
    snapshotId: string
  ): Promise<void> {
    try {
      const docRef = this.getSnapshotDocumentById(user, snapshotId)

      await updateDoc(docRef, {
        emailSentAt: serverTimestamp(),
      })
    } catch (error) {
      console.error('Error updating email sent timestamp:', error)
      throw new Error('Det gick inte att uppdatera email-status')
    }
  }
}
